import * as React from 'react';
import { DayPicker, DropdownProps } from 'react-day-picker';
import { ru } from 'date-fns/locale';
import { format } from 'date-fns';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = ru,
  fromYear,
  toYear,
  ...props
}: CalendarProps) {
  const currentYear = new Date().getFullYear();
  const defaultFromYear = fromYear || currentYear - 100;
  const defaultToYear = toYear || currentYear + 5;

  const CustomDropdown = React.useCallback(
    ({ name, value, onChange, ...dropdownProps }: DropdownProps) => {
      const currentDropdownValue = value?.toString();

      const handleValueChange = (newValue: string) => {
        if (onChange) {
          const changeEvent = {
            target: { value: newValue },
          } as React.ChangeEvent<HTMLSelectElement>;
          onChange(changeEvent);
        }
      };

      let options: React.ReactNode[] = [];
      let placeholder = '';
      const selectWidth = 'w-auto';

      if (name === 'months') {
        placeholder = 'Месяц';
        const months = Array.from({ length: 12 }, (_, i) => ({
          value: i.toString(),
          label: format(new Date(2000, i), 'LLLL', { locale }),
        }));
        options = months.map(month => (
          <SelectItem key={month.value} value={month.value}>
            {month.label.charAt(0).toUpperCase() + month.label.slice(1)}
          </SelectItem>
        ));
      } else if (name === 'years') {
        placeholder = 'Год';
        const startYear = fromYear || defaultFromYear;
        const endYear = toYear || defaultToYear;
        const years = [];
        for (let i = endYear; i >= startYear; i--) {
          years.push({ value: i.toString(), label: i.toString() });
        }
        options = years.map(year => (
          <SelectItem key={year.value} value={year.value}>
            {year.label}
          </SelectItem>
        ));
      }

      return (
        <Select
          onValueChange={handleValueChange}
          value={currentDropdownValue}
          {...dropdownProps}
        >
          <SelectTrigger
            className={cn(
              'h-7 text-xs p-1 border-none bg-transparent focus:ring-0 focus:ring-offset-0 shadow-none font-medium',
              selectWidth
            )}
            hideIcon
          >
            <SelectValue placeholder={placeholder} />
            {name === 'years' && (
              <ChevronRightIcon className="h-4 w-4 text-text-icon-tertiary" />
            )}
          </SelectTrigger>
          <SelectContent className="max-h-[200px] overflow-y-auto">
            {options}
          </SelectContent>
        </Select>
      );
    },
    [locale, fromYear, defaultFromYear, toYear, defaultToYear]
  );

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3 pl-0', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-between items-center h-10 px-1 relative',
        caption_label: 'text-base font-semibold hidden',
        caption_dropdowns: 'flex items-center gap-1',
        nav: 'flex items-center gap-1',
        nav_button: cn(buttonVariants({ variant: 'outline' }), 'h-7 w-7 p-0'),
        nav_button_previous: 'border-none bg-transparent shadow-none',
        nav_button_next: 'border-none bg-transparent shadow-none',
        table: 'w-full border-collapse mt-2',
        head_row: 'flex justify-around mb-2',
        head_cell:
          'text-muted-foreground rounded-md w-9 font-medium text-[0.8rem] uppercase',
        row: 'flex w-full mt-2 justify-around',
        cell: cn(
          'relative p-0 text-center text-sm h-9 w-9 flex items-center justify-center',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md'
            : '[&:has([aria-selected])]:rounded-full'
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal rounded-full flex items-center justify-center'
        ),
        day_selected: 'bg-[#E5F6E9] text-brand-green rounded-full',
        day_today: 'bg-accent text-brand-green rounded-full font-semibold',
        day_outside: 'day-outside text-muted-foreground opacity-30',
        day_disabled: 'text-muted-foreground opacity-50',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ className: c }) => (
          <ChevronLeftIcon
            className={cn('h-6 w-6 text-text-icon-tertiary', c)}
          />
        ),
        IconRight: ({ className: c }) => (
          <ChevronRightIcon
            className={cn('h-6 w-6 text-text-icon-tertiary', c)}
          />
        ),
        Dropdown: CustomDropdown,
      }}
      locale={locale}
      captionLayout="dropdown-buttons"
      fromYear={defaultFromYear}
      toYear={defaultToYear}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
